<script lang="ts" setup>
import ArrowRight from "@/assets/icons/arrow.svg?component";
import type {
  ComponentHeaderCampaignFragment,
  Enum_Componentheaderheadercampaign_Imageposition,
} from "~~/types/graphql";

import { Enum_Componentheaderheadercampaign_Variation } from "~~/types/graphql";

const props = defineProps<{
  headline: ComponentHeaderCampaignFragment["headline"];
  text: ComponentHeaderCampaignFragment["text"];
  variation: Enum_Componentheaderheadercampaign_Variation;
  tag: ComponentHeaderCampaignFragment["tag"];
  image: ComponentHeaderCampaignFragment["image"];
  primaryCta: ComponentHeaderCampaignFragment["primaryCta"];
  primaryCtaAnchor: ComponentHeaderCampaignFragment["primaryCtaAnchor"];
  primaryCtaText: ComponentHeaderCampaignFragment["primaryCtaText"];
  secondaryCta: ComponentHeaderCampaignFragment["secondaryCta"];
  secondaryCtaAnchor: ComponentHeaderCampaignFragment["secondaryCtaAnchor"];
  secondaryCtaText: ComponentHeaderCampaignFragment["secondaryCtaText"];
  imagePosition: Enum_Componentheaderheadercampaign_Imageposition;
}>();

if (props.variation === Enum_Componentheaderheadercampaign_Variation.Light) {
  useHeaderColor("white");
} else {
  useHeaderColor("black");
}
</script>

<template>
  <header
    class="header-campaign"
    :class="{
      'bg-integer-white text-integer-black':
        variation === Enum_Componentheaderheadercampaign_Variation.Light,
      'bg-integer-black text-integer-white':
        variation === Enum_Componentheaderheadercampaign_Variation.Dark,
    }"
  >
    <div class="outer-space container">
      <div
        class="grid grid-cols-1 items-center pb-23 pt-25 md:grid-cols-12 md:pt-30"
      >
        <div
          class="mt-5.6 flex flex-col justify-center md:col-span-6 md:row-start-1 md:mt-0"
          :class="{
            'md:col-start-1 md:col-end-7': imagePosition === 'right',
            'md:col-start-7 md:col-end-13': imagePosition === 'left',
          }"
        >
          <base-tag
            v-if="tag"
            class="w-fit"
            :type="
              variation === Enum_Componentheaderheadercampaign_Variation.Dark
                ? 'tertiary'
                : 'primary'
            "
            >{{ tag }}</base-tag
          >
          <h1
            class="hyphens-auto font-pixel text-h2 lg:text-h1 2xl:text-h1max"
            :class="{
              'mt-5.2': tag,
            }"
          >
            {{ headline }}
          </h1>
          <base-text v-if="text" class="mt-5.4 text-normal" :Text="text" />
          <div
            v-if="
              primaryCta?.data?.attributes?.Slug ||
              secondaryCta?.data?.attributes?.Slug
            "
            class="mt-5.8 flex flex-col gap-5.3 xs:flex-row"
          >
            <base-button
              v-if="primaryCta?.data?.attributes?.Slug && primaryCtaText"
              class="w-full justify-center xs:w-auto"
              :type="
                variation === Enum_Componentheaderheadercampaign_Variation.Dark
                  ? 'secondary'
                  : 'primary'
              "
              :outline="
                variation === Enum_Componentheaderheadercampaign_Variation.Light
                  ? 'gray'
                  : 'white'
              "
              :to="
                primaryCtaAnchor
                  ? primaryCta.data?.attributes?.Slug + '#' + primaryCtaAnchor
                  : primaryCta.data?.attributes?.Slug
              "
              >{{ primaryCtaText }}
              <template #icon-right><arrow-right /></template>
            </base-button>
            <base-button
              v-if="secondaryCta?.data?.attributes?.Slug && secondaryCtaText"
              class="w-full justify-center xs:w-auto"
              :type="
                variation === Enum_Componentheaderheadercampaign_Variation.Dark
                  ? 'primary'
                  : 'secondary'
              "
              :outline="
                variation === Enum_Componentheaderheadercampaign_Variation.Dark
                  ? 'white'
                  : 'black'
              "
              :to="
                secondaryCtaAnchor
                  ? secondaryCta.data?.attributes?.Slug +
                    '#' +
                    secondaryCtaAnchor
                  : secondaryCta.data?.attributes?.Slug
              "
              >{{ secondaryCtaText }}</base-button
            >
          </div>
        </div>
        <div
          v-if="props.image?.data?.attributes"
          class="row-start-1 flex items-center justify-center"
          :class="{
            'md:col-start-8 md:col-end-13': props.imagePosition === 'right',
            'md:col-start-1 md:col-end-6': props.imagePosition === 'left',
          }"
        >
          <base-image
            class="object-contain"
            v-bind="props.image?.data?.attributes"
            sizes="(min-width: 1024px) 45vw, (max-width: 1023px) 100vw"
            :eager-loading="true"
          />
        </div>
      </div>
    </div>
  </header>
</template>
